import { useState } from "react";
import { useTranslation } from "react-i18next";
import { inputCls, labelCls } from "components/tw/classes";
import { updateFinvaldaSettings } from "services/company.service";
import { CompanyEntity } from "types/Company";
import { ChangeLink, EditPanel, SettingsCard, useSaveWithToast } from "../ui";
import {
  SaveLock,
  finvaldaSaveErrorKey,
  storedFinvaldaRequest,
} from "./finvalda-request";

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "password";
  placeholder?: string;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        className={inputCls}
        value={value}
        placeholder={placeholder}
        // A saved login must not be filled into the password field by the browser.
        autoComplete={type === "password" ? "new-password" : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

interface ConnectionFormProps {
  company: CompanyEntity;
  onRefresh: () => Promise<unknown>;
  lock: SaveLock;
  onDone: () => void;
}

// Mounted only while editing, so closing it throws away the draft and any
// error from the last attempt.
function ConnectionForm({
  company,
  onRefresh,
  lock,
  onDone,
}: ConnectionFormProps) {
  const { t } = useTranslation();
  const { save, error } = useSaveWithToast();
  const stored = storedFinvaldaRequest(company);
  const hasStoredPassword = Boolean(company.finvaldaPass);
  const [draft, setDraft] = useState({
    address: stored.finvaldaAddress,
    companyId: stored.finvaldaCompanyId,
    user: stored.finvaldaUser,
    password: "",
  });
  const [problem, setProblem] = useState<string | null>(null);
  const change = (changes: Partial<typeof draft>) => {
    setDraft({ ...draft, ...changes });
    setProblem(null);
  };

  // The server keeps the stored password for an empty field only while the
  // login it belongs to is unchanged.
  const isLoginChanged =
    draft.address.trim() !== stored.finvaldaAddress ||
    draft.companyId.trim() !== stored.finvaldaCompanyId ||
    draft.user !== stored.finvaldaUser;
  const canKeepStoredPassword = hasStoredPassword && !isLoginChanged;

  const submit = async () => {
    const isFieldMissing =
      !draft.address.trim() || !draft.companyId.trim() || !draft.user;
    const isPasswordEmpty = draft.password === "";
    if (isFieldMissing || (isPasswordEmpty && !hasStoredPassword)) {
      setProblem(t("settings_finvalda_fields_required"));
      return;
    }
    if (isPasswordEmpty && isLoginChanged) {
      setProblem(t("finvalda_password_required"));
      return;
    }

    const saved = await lock.hold(() =>
      save(
        async () => {
          await updateFinvaldaSettings(
            {
              ...stored,
              finvaldaAddress: draft.address.trim(),
              finvaldaCompanyId: draft.companyId.trim(),
              finvaldaUser: draft.user,
              finvaldaPass: draft.password,
            },
            company.id
          );
          await onRefresh();
        },
        { errorMessage: (err) => t(finvaldaSaveErrorKey(err)) }
      )
    );
    if (saved) onDone();
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onDone}
      saving={lock.isLocked}
      error={problem ?? error}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          id="finvalda-address"
          label={t("finvaldaAddress")}
          value={draft.address}
          onChange={(address) => change({ address })}
        />
        <Field
          id="finvalda-company-id"
          label={t("finvaldaCompanyId")}
          value={draft.companyId}
          onChange={(companyId) => change({ companyId })}
        />
        <Field
          id="finvalda-user"
          label={t("finvaldaUser")}
          value={draft.user}
          onChange={(user) => change({ user })}
        />
        <Field
          id="finvalda-password"
          type="password"
          label={t("finvaldaPass")}
          value={draft.password}
          placeholder={
            canKeepStoredPassword ? t("finvalda_pass_keep_placeholder") : ""
          }
          onChange={(password) => change({ password })}
        />
      </div>
    </EditPanel>
  );
}

/** How ERP logs in to Finvalda. The password is never shown, only whether one is saved. */
export default function FinvaldaConnectionCard({
  company,
  onRefresh,
  lock,
}: {
  company: CompanyEntity;
  onRefresh: () => Promise<unknown>;
  lock: SaveLock;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  const values = [
    { label: t("finvaldaAddress"), value: company.finvaldaAddress },
    { label: t("finvaldaCompanyId"), value: company.finvaldaCompanyId },
    { label: t("finvaldaUser"), value: company.finvaldaUser },
    {
      label: t("finvaldaPass"),
      value: company.finvaldaPass ? t("settings_finvalda_password_saved") : "",
    },
  ];

  return (
    <SettingsCard
      title={t("settings_finvalda_connection_title")}
      description={t("settings_finvalda_connection_lead")}
      actions={!editing && <ChangeLink onClick={() => setEditing(true)} />}
    >
      {editing ? (
        <ConnectionForm
          company={company}
          onRefresh={onRefresh}
          lock={lock}
          onDone={() => setEditing(false)}
        />
      ) : (
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {values.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-xs text-gray-500">{label}</dt>
              <dd className="mt-0.5 break-words text-sm text-gray-900">
                {value || (
                  <span className="text-gray-500">{t("settings_none")}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </SettingsCard>
  );
}
